import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";

const createInitialForm = () => {
  const now = new Date();

  const localDate = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
  ].join("-");

  return {
    agent_id: "",
    amount: "",
    currency: "USD",
    period_month: String(now.getMonth() + 1),
    period_year: String(now.getFullYear()),
    payment_date: localDate,
    note: "",
  };
};

const SalaryPaymentForm = ({ onCreated }) => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [agents, setAgents] = useState([]);
  const [form, setForm] = useState(createInitialForm);
  const [loadingRefs, setLoadingRefs] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const assignedAgencyLabel = useMemo(() => {
    if (!user?.agency_id) return "";

    return [
      user?.agency_name || "",
      user?.city || user?.location_city || "",
      user?.country || user?.location_country || "",
    ]
      .filter(Boolean)
      .join(" • ");
  }, [
    user?.agency_id,
    user?.agency_name,
    user?.city,
    user?.location_city,
    user?.country,
    user?.location_country,
  ]);

  useEffect(() => {
    let active = true;

    const fetchAgents = async () => {
      if (!user?.agency_id) {
        setAgents([]);
        return;
      }

      try {
        setLoadingRefs(true);

        const response = await api.get("/admin/agents", {
          params: {
            location_id: user?.location_id || undefined,
            page: 1,
            limit: 500,
          },
        });

        const rows = Array.isArray(response.data?.agents)
          ? response.data.agents
          : [];

        const sameAgency = rows.filter(
          (agent) =>
            Number(agent.agency_id) === Number(user.agency_id)
        );

        if (active) {
          setAgents(sameAgency);
        }
      } catch (error) {
        console.error("Agent loading error:", error);

        if (active) {
          setAgents([]);

          enqueueSnackbar("Échec du chargement des agents", {
            variant: "error",
          });
        }
      } finally {
        if (active) {
          setLoadingRefs(false);
        }
      }
    };

    fetchAgents();

    return () => {
      active = false;
    };
  }, [
    enqueueSnackbar,
    user?.agency_id,
    user?.location_id,
  ]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((previousForm) => ({
      ...previousForm,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (submitting) return;

    const {
      agent_id,
      amount,
      currency,
      period_month,
      period_year,
      payment_date,
      note,
    } = form;

    if (!user?.agency_id) {
      enqueueSnackbar("Votre compte n’a pas d’agence assignée", {
        variant: "error",
      });
      return;
    }

    if (
      !agent_id ||
      !amount ||
      !currency ||
      !period_month ||
      !period_year
    ) {
      enqueueSnackbar(
        "Tous les champs obligatoires doivent être remplis",
        { variant: "warning" }
      );
      return;
    }

    const numericAgentId = Number(agent_id);
    const numericAgencyId = Number(user.agency_id);
    const numericAmount = Number(amount);
    const numericMonth = Number(period_month);
    const numericYear = Number(period_year);
    const normalizedCurrency = String(currency)
      .trim()
      .toUpperCase();

    const selectedAgent = agents.find(
      (agent) => Number(agent.id) === numericAgentId
    );

    if (!selectedAgent) {
      enqueueSnackbar(
        "L’agent sélectionné n’appartient pas à cette agence",
        { variant: "warning" }
      );
      return;
    }

    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      enqueueSnackbar("Montant invalide", {
        variant: "warning",
      });
      return;
    }

    if (
      !Number.isInteger(numericMonth) ||
      numericMonth < 1 ||
      numericMonth > 12
    ) {
      enqueueSnackbar("Mois invalide", {
        variant: "warning",
      });
      return;
    }

    if (
      !Number.isInteger(numericYear) ||
      numericYear < 2000 ||
      numericYear > 2100
    ) {
      enqueueSnackbar("Année invalide", {
        variant: "warning",
      });
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        agent_id: numericAgentId,
        agency_id: numericAgencyId,
        amount: numericAmount,
        currency: normalizedCurrency,
        period_month: numericMonth,
        period_year: numericYear,
        payment_date: payment_date || null,
        note: note.trim() || null,
      };

      const response = await api.post(
        "/salary-payments",
        payload
      );

      enqueueSnackbar("Paiement enregistré avec succès", {
        variant: "success",
      });

      setForm(createInitialForm());

      if (typeof onCreated === "function") {
        onCreated(response.data);
      }
    } catch (error) {
      console.error("Salary payment error:", error);

      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Échec de l’enregistrement du salaire";

      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const hasAgency = Boolean(user?.agency_id);
  const hasAgents = agents.length > 0;

  return (
    <Paper
      component="form"
      onSubmit={handleSubmit}
      sx={{ p: 3, borderRadius: 3 }}
    >
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
        Nouveau paiement
      </Typography>

      <Alert severity={hasAgency ? "info" : "warning"} sx={{ mb: 2 }}>
        <strong>Agence :</strong>{" "}
        {assignedAgencyLabel ||
          `Agence #${user?.agency_id || "non définie"}`}
      </Alert>

      {!loadingRefs && hasAgency && !hasAgents && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Aucun agent n’est disponible pour cette agence.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            select
            required
            fullWidth
            label="Agent"
            name="agent_id"
            value={form.agent_id}
            onChange={handleChange}
            disabled={loadingRefs || submitting || !hasAgency}
            helperText={
              loadingRefs
                ? "Chargement des agents..."
                : "Sélectionnez l’agent payé"
            }
          >
            {agents.map((agent) => (
              <MenuItem key={agent.id} value={String(agent.id)}>
                {agent.name}
                {agent.email ? ` — ${agent.email}` : ""}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            required
            fullWidth
            label="Montant"
            name="amount"
            type="number"
            value={form.amount}
            onChange={handleChange}
            disabled={submitting}
            inputProps={{
              min: 0.01,
              step: "0.01",
            }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            select
            required
            fullWidth
            label="Devise"
            name="currency"
            value={form.currency}
            onChange={handleChange}
            disabled={submitting}
          >
            <MenuItem value="USD">USD</MenuItem>
            <MenuItem value="CDF">CDF</MenuItem>
            <MenuItem value="RMB">RMB</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Date de paiement"
            name="payment_date"
            type="date"
            value={form.payment_date}
            onChange={handleChange}
            disabled={submitting}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            select
            required
            fullWidth
            label="Mois"
            name="period_month"
            value={form.period_month}
            onChange={handleChange}
            disabled={submitting}
          >
            {Array.from({ length: 12 }, (_, index) => {
              const month = index + 1;

              return (
                <MenuItem key={month} value={String(month)}>
                  {month}
                </MenuItem>
              );
            })}
          </TextField>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            label="Année"
            name="period_year"
            type="number"
            value={form.period_year}
            onChange={handleChange}
            disabled={submitting}
            inputProps={{
              min: 2000,
              max: 2100,
              step: 1,
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Note"
            name="note"
            value={form.note}
            onChange={handleChange}
            disabled={submitting}
            inputProps={{ maxLength: 1000 }}
          />
        </Grid>
      </Grid>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mt: 2.5,
        }}
      >
        <Button
          type="submit"
          variant="contained"
          disabled={
            submitting ||
            loadingRefs ||
            !hasAgency ||
            !hasAgents
          }
          startIcon={
            submitting ? (
              <CircularProgress size={18} color="inherit" />
            ) : null
          }
        >
          {submitting ? "Enregistrement..." : "Enregistrer"}
        </Button>
      </Box>
    </Paper>
  );
};

export default SalaryPaymentForm;